"use client"

import { useState } from "react"
import { Trash, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { deleteAllPosts } from "lib/api/notes"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"

/**
 * DeleteAllPosts component
 *
 * Provides a UI for permanently deleting all posts in a publication.
 * Displays a danger warning with a confirmation dialog to prevent accidental deletion.
 * Handles the API call to delete all posts and displays appropriate toast notifications.
 */
export default function DeleteAllPosts() {
  const [isLoading, setIsLoading] = useState(false)

  /**
   * Handles the deletion of all posts when confirmed by the user
   * Makes API call and manages loading state and toast notifications
   */
  const handleDeleteAllPosts = async () => {
    try {
      setIsLoading(true)

      // Show loading toast
      const toastId = toast.loading("Deleting all posts...")

      // Call the API client function to delete all posts
      const result = await deleteAllPosts()

      if (result.success) {
        // Show success toast
        toast.success(
          result.message || "All posts have been deleted successfully",
          {
            id: toastId,
            duration: 3000,
          }
        )
      } else {
        // Show error toast
        toast.error(
          result.message || "Failed to delete posts. Please try again.",
          {
            id: toastId,
            duration: 3000,
          }
        )
      }
    } catch (error) {
      // Show error toast for unexpected errors
      toast.error("An unexpected error occurred. Please try again.", {
        duration: 3000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg ">Delete All Posts</h3>

      <Alert
        variant="destructive"
        className="flex flex-col sm:flex-row items-start sm:items-center gap-4"
      >
        <div className="flex-grow">
          <div className="flex items-center gap-2">
            <Trash className="h-4 w-4" />
            <AlertTitle className="mt-0.5">Danger Zone</AlertTitle>
          </div>
          <AlertDescription className="mt-1">
            This will permanently delete all of your posts, including ones you
            created manually. This might be useful if your import went wrong.
            This action cannot be undone.
          </AlertDescription>
        </div>

        <AlertDialog>
          <div className="flex justify-center sm:justify-start w-full sm:w-auto mt-2 sm:mt-0">
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="flex-shrink-0">
                Delete All Posts
              </Button>
            </AlertDialogTrigger>
          </div>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete all
                of your posts and remove your data from our servers.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAllPosts}
                disabled={isLoading}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Deleting...
                  </span>
                ) : (
                  "Delete All Posts"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Alert>
    </div>
  )
}
